/**
 * LessonAttachments (2026-10-02) — tài liệu đính kèm bài học (PDF/PowerPoint/Word).
 *
 * - Học viên (`editable` = false): danh sách + nút tải xuống; tài liệu đầu tiên
 *   tự hiển thị ngay trên trang (khung xem PDF — PPT/Word được BE chuyển sang
 *   PDF bằng LibreOffice), bấm "Xem" để đổi tài liệu.
 * - Giảng viên/admin (`editable`): thêm tải lên + xoá (trang LessonEditor).
 *
 * File tải qua apiClient (kèm JWT) dạng blob — BE chặn 403 `not_enrolled`
 * nếu chưa đăng ký môn (lesson không phải preview).
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  List,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  ExpandOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileWordOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_MB,
  type LessonAttachment,
  deleteLessonAttachment,
  downloadLessonAttachment,
  fetchLessonAttachmentPreview,
  formatFileSize,
  uploadLessonAttachment,
} from "@/api/content";

const { Text } = Typography;

function FileIcon({ ext }: { ext: string }) {
  if (ext === ".pdf") return <FilePdfOutlined style={{ fontSize: 22, color: "#cf1322" }} />;
  if (ext === ".ppt" || ext === ".pptx")
    return <FilePptOutlined style={{ fontSize: 22, color: "#d4380d" }} />;
  if (ext === ".doc" || ext === ".docx")
    return <FileWordOutlined style={{ fontSize: 22, color: "#1d39c4" }} />;
  return <FileOutlined style={{ fontSize: 22 }} />;
}

function errorDetail(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  const fileErr = data.file;
  if (Array.isArray(fileErr) && typeof fileErr[0] === "string") return fileErr[0];
  return fallback;
}

/** Blob error response (responseType=blob) → đọc JSON để lấy `detail`. */
async function blobErrorDetail(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      if (typeof json.detail === "string") return json.detail;
    } catch {
      /* không phải JSON */
    }
  }
  return fallback;
}

interface Props {
  attachments: LessonAttachment[];
  /** Bật chế độ giảng viên: tải lên + xoá. Bắt buộc có `lessonSlug`. */
  editable?: boolean;
  lessonSlug?: string;
  onChanged?: () => void;
}

export function LessonAttachments({ attachments, editable = false, lessonSlug, onChanged }: Props) {
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // Học viên: mở sẵn tài liệu đầu tiên. Giảng viên: đóng, bấm "Xem" để kiểm tra.
  const [viewingId, setViewingId] = useState<number | null>(
    editable ? null : (attachments[0]?.id ?? null),
  );
  const viewing = attachments.find((a) => a.id === viewingId);
  const [messageApi, contextHolder] = message.useMessage();

  const handleUpload = async (file: File) => {
    if (!lessonSlug) return;
    if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
      messageApi.error(`File quá lớn — tối đa ${ATTACHMENT_MAX_MB} MB.`);
      return;
    }
    setUploadPercent(0);
    try {
      await uploadLessonAttachment(lessonSlug, file, setUploadPercent);
      messageApi.success(`Đã tải lên "${file.name}"`);
      onChanged?.();
    } catch (err) {
      messageApi.error(errorDetail(err, "Tải lên thất bại"));
    } finally {
      setUploadPercent(null);
    }
  };

  const handleDownload = async (item: LessonAttachment) => {
    setDownloadingId(item.id);
    try {
      await downloadLessonAttachment(item);
    } catch (err) {
      messageApi.error(await blobErrorDetail(err, "Không tải được tài liệu"));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (item: LessonAttachment) => {
    try {
      await deleteLessonAttachment(item.id);
      messageApi.success("Đã xoá tài liệu");
      onChanged?.();
    } catch (err) {
      messageApi.error(errorDetail(err, "Xoá thất bại"));
    }
  };

  return (
    <div data-testid="lesson-attachments">
      {contextHolder}
      {editable && (
        <Space direction="vertical" style={{ width: "100%", marginBottom: 12 }}>
          <Upload
            accept={ATTACHMENT_ACCEPT}
            multiple
            showUploadList={false}
            disabled={uploadPercent !== null}
            beforeUpload={(file) => {
              handleUpload(file);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploadPercent !== null}>
              Tải lên tài liệu
            </Button>
          </Upload>
          <Text type="secondary" style={{ fontSize: 12 }}>
            PDF, PowerPoint (.ppt, .pptx), Word (.doc, .docx) — tối đa {ATTACHMENT_MAX_MB} MB/file.
          </Text>
          {uploadPercent !== null && <Progress percent={uploadPercent} size="small" />}
        </Space>
      )}

      <List
        size="small"
        locale={{ emptyText: "Chưa có tài liệu đính kèm" }}
        dataSource={attachments}
        renderItem={(item) => (
          <List.Item
            style={item.id === viewingId ? { background: "rgba(22, 119, 255, 0.06)" } : undefined}
            actions={[
              <Button
                key="view"
                type="link"
                icon={item.id === viewingId ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setViewingId(item.id === viewingId ? null : item.id)}
              >
                {item.id === viewingId ? "Ẩn" : "Xem"}
              </Button>,
              <Button
                key="download"
                type="link"
                icon={<DownloadOutlined />}
                loading={downloadingId === item.id}
                onClick={() => handleDownload(item)}
              >
                Tải xuống
              </Button>,
              ...(editable
                ? [
                    <Popconfirm
                      key="delete"
                      title="Xoá tài liệu này?"
                      okText="Xoá"
                      cancelText="Huỷ"
                      onConfirm={() => handleDelete(item)}
                    >
                      <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]
                : []),
            ]}
          >
            <List.Item.Meta
              avatar={<FileIcon ext={item.file_ext} />}
              title={
                <Text ellipsis={{ tooltip: item.original_filename }}>{item.original_filename}</Text>
              }
              description={formatFileSize(item.size_bytes)}
            />
          </List.Item>
        )}
      />

      {viewing && (
        <AttachmentViewer
          key={viewing.id}
          attachment={viewing}
          downloading={downloadingId === viewing.id}
          onDownload={() => handleDownload(viewing)}
        />
      )}
    </div>
  );
}

// ─── Khung xem PDF ngay trên trang ─────────────────────────────────────────

interface ViewerProps {
  attachment: LessonAttachment;
  downloading: boolean;
  onDownload: () => void;
}

function AttachmentViewer({ attachment, downloading, onDownload }: ViewerProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchLessonAttachmentPreview(attachment.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(async (err) => {
        if (!cancelled) setError(await blobErrorDetail(err, "Không hiển thị được tài liệu"));
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);

  if (error) {
    return (
      <Alert
        style={{ marginTop: 12 }}
        type="warning"
        showIcon
        message={error}
        action={
          <Button
            size="small"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={onDownload}
          >
            Tải xuống
          </Button>
        }
      />
    );
  }

  if (!url) {
    return (
      <div style={{ marginTop: 12, padding: 48, textAlign: "center" }}>
        <Spin tip="Đang tải tài liệu..." />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }} data-testid="attachment-viewer">
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
        <Text strong ellipsis={{ tooltip: attachment.original_filename }}>
          {attachment.original_filename}
        </Text>
        <Button size="small" icon={<ExpandOutlined />} onClick={() => window.open(url, "_blank")}>
          Mở toàn màn hình
        </Button>
      </Space>
      <iframe
        src={url}
        title={attachment.original_filename}
        style={{
          width: "100%",
          height: "min(75vh, 760px)",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 8,
        }}
      />
    </div>
  );
}
